import React, {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import ct from "class-types.macro";
import GoBackButton from "./Buttons/GoBackButton";
import ContinueButton from "./Buttons/ContinueButton";
import GeneralContactForm, {
  ContactFormData,
  DefaultFormData,
} from "./NextSteps/ContactCustomerService/GeneralContactForm";
import ErrorAlert from "../../../common/Alert/ErrorAlert";
import convertJsonToFormData from "../../../common/Form/FormHelpers/convertJsonToFormData";

export type StepAndAnswer = {
  step: number;
  answers: Answer[];
};

export type Answer = {
  questionId: number;
  answerId: number;
};

type ContactAnswer = {
  answerId: number;
  text: string;
  displayOrder: number;
};

type ContactQuestion = {
  questionId: number;
  text: string;
  isRequired: boolean;
  answers: ContactAnswer[];
};

export type ContactStep = {
  step: number;
  text: string;
  showGoodAnswer: boolean;
  showResponseForm: boolean;
  showFileUpload: boolean;
  questions: ContactQuestion[];
};

interface Props {
  contactId: number;
  currentStep: ContactStep;
  setCurrentStep: Dispatch<SetStateAction<ContactStep>>;
  defaultFormData: DefaultFormData;
  setHasSubmitted: Dispatch<SetStateAction<boolean>>;
  stepAndAnswers: readonly StepAndAnswer[];
  setStepAndAnswers: React.Dispatch<
    React.SetStateAction<readonly StepAndAnswer[]>
  >;
}

const stepStack: ContactStep[] = [];

const Step = ({
  contactId,
  currentStep,
  setCurrentStep,
  defaultFormData,
  setHasSubmitted,
  stepAndAnswers,
  setStepAndAnswers,
}: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorState, setErrorState] = useState<string>();
  const stepFormRef = useRef<HTMLFormElement>(null);
  const errorWrapper = useRef<HTMLDivElement>(null);
  const elementToFocus = useRef<HTMLLegendElement>(null);

  useEffect(() => {
    errorWrapper.current?.scrollIntoView({ behavior: "smooth" });
  }, [errorState]);

  // Accessibility
  useEffect(() => {
    if (!currentStep.showResponseForm) {
      setTimeout(() => {
        elementToFocus.current?.focus();
      }, 10);
    }
    if (currentStep.text) {
      document.getElementById("gcf-text")?.focus();
    }
    if (currentStep.showResponseForm) {
      document.getElementById("gcf-name")?.focus();
    }
  }, [currentStep]);

  const submitStep: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (isProcessing) {
      return;
    }
    setIsProcessing(true);

    setErrorState(undefined);
    const data = new FormData(e.currentTarget);
    const body = [];
    // Validate
    for (const q of currentStep.questions) {
      if (q.isRequired && !data.get(`q:${q.questionId}`)) {
        setErrorState("A response is required");
        setIsProcessing(false);
        return;
      }
    }
    for (const [key, value] of data.entries()) {
      body.push({
        questionId: Number(key.replace("q:", "")),
        answerId: Number(value),
      });
    }

    submitContactAnswer(body);
    setIsProcessing(false);
  };

  const goBack = () => {
    if (stepStack.length > 0) {
      const previousStep = stepStack.pop();
      if (previousStep) {
        setErrorState(undefined);
        setCurrentStep(previousStep);

        const currentStepAndAnswers = [...stepAndAnswers];
        if (currentStepAndAnswers.pop()) {
          setStepAndAnswers(currentStepAndAnswers);
        }
      }
    }
  };

  const attemptToGoToNext = () => {
    if (stepFormRef?.current) {
      stepFormRef?.current.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  };

  const submitDidThisHelpAnswer = (didHelp: boolean) => {
    const question = currentStep.questions[0];
    if (question) {
      submitContactAnswer([
        {
          questionId: question.questionId ?? 0,
          answerId: question.answers[didHelp ? 0 : 1]?.answerId ?? 0,
        },
      ]);
      if (didHelp) {
        setHasSubmitted(true);
      }
    }
  };

  const submitContactAnswer = async (answers: Answer[]) => {
    if (isProcessing) {
      return;
    }
    setIsProcessing(true);

    try {
      const response = await fetch("/api/support/contactanswer/", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          contactId,
          stepId: currentStep.step,
          answers,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit answer, status: ${response.status}`);
      }

      stepStack.push(currentStep);
      const updatedStepAndAnswers = [
        ...stepAndAnswers,
        {
          step: currentStep.step,
          answers,
        },
      ];
      const nextStep = await response.json<ContactStep>();
      setStepAndAnswers(updatedStepAndAnswers);
      setCurrentStep(nextStep);
    } catch {
      setErrorState(
        "There was an issue submitting your answer. Please try again in a few minutes"
      );
    }

    setIsProcessing(false);
  };

  const submitContactForm = async (formData: ContactFormData) => {
    if (isProcessing) {
      return;
    }
    setIsProcessing(true);
    const targetData = convertJsonToFormData(formData);

    try {
      const response = await fetch("/api/support/getintouch/", {
        method: "POST",
        body: targetData,
      });

      if (!response.ok) {
        throw new Error(
          `Error submitting support form, status: ${response.status}`
        );
      }

      setErrorState(undefined);
      setHasSubmitted(true);
      const nextStep = await response.json<ContactStep>();
      setCurrentStep(nextStep);
    } catch {
      setErrorState(
        "There was an issue submitting the form. Please try again in a few minutes"
      );
    }

    setIsProcessing(false);
  };

  return (
    <>
      <div aria-live="assertive">
        {errorState && (
          <ErrorAlert ref={errorWrapper} id="gcf-errorMessage" className="mb-6">
            {errorState}
          </ErrorAlert>
        )}
      </div>
      {currentStep.text && currentStep.text.length > 0 && (
        <div
          id="gcf-text"
          tabIndex={-1}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: currentStep.text }}
        />
      )}
      {!currentStep.showGoodAnswer &&
        currentStep.questions &&
        currentStep.questions.length > 0 && (
          <>
            <form onSubmit={submitStep} ref={stepFormRef}>
              {currentStep.questions.map((q, index) => (
                <div className="pb-4" key={q.questionId}>
                  <fieldset
                    aria-describedby="gcf-errorMessage"
                    aria-required
                    tabIndex={-1}
                  >
                    <legend
                      tabIndex={-1}
                      id="gcf-question"
                      className="m-0 pb-3 font-semibold text-sm text-gray-800 focus:outline-none"
                      ref={index === 0 ? elementToFocus : undefined}
                    >
                      {q.text}{" "}
                      {q.isRequired ? (
                        <>
                          *<span className="sr-only"> required</span>
                        </>
                      ) : (
                        ""
                      )}
                    </legend>
                    {q.answers
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((a) => (
                        <label
                          key={`${q.questionId}:${a.answerId}`}
                          className={ct(
                            "flex",
                            "font-normal",
                            "relative",
                            "text-gray-800",
                            "w-full",
                            "md:w-1/2",
                            "lt:w-full",
                            "lg:w-1/2"
                          )}
                        >
                          <input
                            type="radio"
                            name={`q:${q.questionId}`}
                            value={a.answerId}
                            className="inline-block align-top"
                            style={{ minHeight: "13px", minWidth: "13px" }}
                          />
                          <span
                            className={ct(
                              "font-normal",
                              "text-sm",
                              "leading-tight",
                              "pl-2",
                              "pt-1/2",
                              "text-gray-800"
                            )}
                          >
                            {a.text}
                          </span>
                        </label>
                      ))}
                  </fieldset>
                </div>
              ))}
            </form>
            <div>
              {currentStep.step !== 1 && <GoBackButton backward={goBack} />}
              <ContinueButton forward={attemptToGoToNext} />
            </div>
          </>
        )}
      {currentStep.showGoodAnswer && (
        <>
          <p className="mb-2 text-sm">Was this information helpful?</p>
          <div className="md:flex lt:flex-col lg:flex-row">
            <div className="group">
              <button
                type="button"
                className="relative text-sm bg-white text-gray-800 py-3 px-2 ml-0 mt-0 w-full md:w-auto md:mr-4 lt:w-full lg:w-auto lg:mr-4 rounded border-solid border-2 group-hover:border-green-500"
                onClick={() => submitDidThisHelpAnswer(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  // fill="#4a5568"
                  className="absolute top-2-1/2 fill-current text-gray-700 group-hover:text-green-500"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M14.6 8H21a2 2 0 0 1 2 2v2.104a2 2 0 0 1-.15.762l-3.095 7.515a1 1 0 0 1-.925.619H2a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1h3.482a1 1 0 0 0 .817-.423L11.752.85a.5.5 0 0 1 .632-.159l1.814.907a2.5 2.5 0 0 1 1.305 2.853L14.6 8zM7 10.588V19h11.16L21 12.104V10h-6.4a2 2 0 0 1-1.938-2.493l.903-3.548a.5.5 0 0 0-.261-.571l-.661-.33-4.71 6.672c-.25.354-.57.644-.933.858zM5 11H3v8h2v-8z" />
                </svg>
                <span className="pl-7">Yes, that answered my question!</span>
              </button>
            </div>
            <div className="group">
              <button
                type="button"
                className="relative text-sm text-center bg-white text-gray-800 py-3 px-2 ml-0 mt-2 w-full md:w-auto md:mt-0 lt:w-full lt:mt-2 lg:w-auto lg:mr-4 lg:mt-0 rounded border-solid border-2 group-hover:border-blue-400"
                onClick={() => submitDidThisHelpAnswer(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="#4a5568"
                  className="absolute top-2-1/2 fill-current text-gray-700 group-hover:text-blue-400"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M9.4 16H3a2 2 0 0 1-2-2v-2.104a2 2 0 0 1 .15-.762L4.246 3.62A1 1 0 0 1 5.17 3H22a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-3.482a1 1 0 0 0-.817.423l-5.453 7.726a.5.5 0 0 1-.632.159L9.802 22.4a2.5 2.5 0 0 1-1.305-2.853L9.4 16zm7.6-2.588V5H5.84L3 11.896V14h6.4a2 2 0 0 1 1.938 2.493l-.903 3.548a.5.5 0 0 0 .261.571l.661.33 4.71-6.672c.25-.354.57-.644.933-.858zM19 13h2V5h-2v8z" />
                </svg>
                <span className="pl-7">
                  No, I&#39;d like to contact support
                </span>
              </button>
            </div>
          </div>
        </>
      )}
      {currentStep.showResponseForm && (
        <GeneralContactForm
          defaultFormData={defaultFormData}
          submitContactForm={submitContactForm}
          goBack={goBack}
          isFileUploadForm={currentStep.showFileUpload ?? false}
          answers={stepAndAnswers.map((x) => x.answers).flat()}
        />
      )}
    </>
  );
};

export default Step;
